// src/reports/RelatorioCompletoPage.jsx
import {
  Fragment, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  Typography,
  Box,
  Button,
  Stack,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import dayjs from 'dayjs';

import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import SelectField from '../common/components/SelectField';

// (opcionais – caso queira pré-visualização no mapa)
import MapRoutePath from '../map/MapRoutePath';
import MapRoutePoints from '../map/MapRoutePoints';
import MapPositions from '../map/MapPositions';
import ReportMapPanel from './components/ReportMapPanel';
import ReportMapContent from './components/ReportMapContent';

const STORAGE_KEY = (userId) => `relatorioCompleto.history.${userId || 'anon'}`;

const POLL_INTERVAL_MS = 30000; // 30s
const MAX_ATTEMPTS = 20; // ~10min
const HISTORY_LIMIT = 100;
const REQUEST_TIMEOUT_MS = 45000;

const REPORT_ENDPOINT = import.meta.env.VITE_REPORT_ENDPOINT || '/relatorios/onGerarHTML.php';
const REPORTS_ORIGIN = import.meta.env.VITE_REPORTS_ORIGIN || 'https://relatorios.localizzare.com.br';
const APP_HOSTNAME = import.meta.env.VITE_APP_HOSTNAME || 'app.localizzare.com.br';

function buildViewerPath(url, type) {
  const normalized = normalizeReportUrl(url);
  if (!normalized) return '';
  const params = new URLSearchParams({
    url: normalized,
    type,
  });
  return `/reports/viewer?${params.toString()}`;
}

function buildSelectedColumns(columns) {
  const set = new Set(columns);
  return {
    driver: set.has('driver'),
    speed: set.has('speed'),
    latlon: set.has('latitude') || set.has('longitude'),
    address: set.has('address'),
  };
}

// prioriza ID do device como "placa" (pelo exemplo de cURL)
function resolvePlaca(device, fallbackId) {
  return String(device?.id ?? fallbackId ?? device?.uniqueId ?? device?.name ?? '');
}

function safeJsonParse(str, fallback) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

function normalizeReportUrl(url) {
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      const isBareReportFile = /^\/Relatorio[^/]+\.(html|pdf)$/i.test(parsed.pathname);

      if (parsed.hostname === APP_HOSTNAME && isBareReportFile) {
        const reportFile = parsed.pathname.replace(/^\//, '');
        return new URL(`/output/${reportFile}`, REPORTS_ORIGIN).toString();
      }

      return parsed.toString();
    }

    if (/^\/output\//i.test(trimmed)) {
      return new URL(trimmed, REPORTS_ORIGIN).toString();
    }

    if (/^Relatorio[^/]+\.(html|pdf)(?:[?#].*)?$/i.test(trimmed)) {
      return new URL(`/output/${trimmed}`, REPORTS_ORIGIN).toString();
    }

    return new URL(trimmed, window.location.origin).toString();
  } catch {
    return '';
  }
}

function hasReportExtension(url, extension) {
  return new RegExp(`\\.${extension}(?:$|[?#])`, 'i').test(url || '');
}

function collectStringValues(input, maxDepth = 3) {
  const values = [];
  const stack = [{ value: input, depth: 0 }];

  while (stack.length > 0) {
    const { value, depth } = stack.pop();
    if (depth > maxDepth || !value) continue;

    if (typeof value === 'string') {
      values.push(value);
      continue;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => stack.push({ value: item, depth: depth + 1 }));
      continue;
    }

    if (typeof value === 'object') {
      Object.values(value).forEach((item) => stack.push({ value: item, depth: depth + 1 }));
    }
  }

  return values;
}

function normalizeBackendErrorMessage(rawMessage, t) {
  const msg = String(rawMessage || '').trim();
  if (!msg) return '';
  if (/failed to create pr/i.test(msg)) return t('reportFullFailedCreatePr');
  return msg;
}

/**
 * Agora o backend retorna JSON:
 * { htmlUrl: ".../output/RelatorioViagens_....html", pdfUrl: ".../output/RelatorioViagens_....pdf" }
 */
function extractReportUrls(respText, resp) {
  const out = { htmlUrl: '', pdfUrl: '' };
  const htmlKeys = ['htmlUrl', 'html', 'html_url', 'reportUrl', 'arquivoHtml', 'url', 'link'];
  const pdfKeys = ['pdfUrl', 'pdf', 'pdf_url', 'arquivoPdf', 'reportPdfUrl'];

  // 1) JSON direto (preferência total)
  const j = safeJsonParse(respText, null);
  if (j && typeof j === 'object') {
    const directHtml = htmlKeys
      .map((key) => j?.[key])
      .find((value) => typeof value === 'string' && hasReportExtension(value, 'html'));
    const directPdf = pdfKeys
      .map((key) => j?.[key])
      .find((value) => typeof value === 'string' && hasReportExtension(value, 'pdf'));

    if (directHtml) out.htmlUrl = directHtml;
    if (directPdf) out.pdfUrl = directPdf;

    if (!out.htmlUrl || !out.pdfUrl) {
      const values = collectStringValues(j);
      if (!out.htmlUrl) {
        out.htmlUrl = values.find((value) => hasReportExtension(value, 'html')) || '';
      }
      if (!out.pdfUrl) {
        out.pdfUrl = values.find((value) => hasReportExtension(value, 'pdf')) || '';
      }
    }
  }

  // 2) Location header
  if (!out.htmlUrl) {
    const loc = resp?.headers?.get('Location') || resp?.headers?.get('location');
    if (loc && hasReportExtension(loc, 'html')) out.htmlUrl = loc;
  }
  if (!out.pdfUrl) {
    const loc = resp?.headers?.get('Location') || resp?.headers?.get('location');
    if (loc && hasReportExtension(loc, 'pdf')) out.pdfUrl = loc;
  }

  // 3) Último fallback: procurar URLs .html/.pdf (absolutas ou relativas)
  if ((!out.htmlUrl || !out.pdfUrl) && typeof respText === 'string') {
    if (!out.htmlUrl) {
      const htmlMatch = respText.match(/(?:https?:\/\/|\/)[^\s"'<>]+\.html(?:[?#][^\s"'<>]*)?/i);
      if (htmlMatch?.[0]) out.htmlUrl = htmlMatch[0];
    }
    if (!out.pdfUrl) {
      const pdfMatch = respText.match(/(?:https?:\/\/|\/)[^\s"'<>]+\.pdf(?:[?#][^\s"'<>]*)?/i);
      if (pdfMatch?.[0]) out.pdfUrl = pdfMatch[0];
    }
  }

  return {
    htmlUrl: normalizeReportUrl(out.htmlUrl),
    pdfUrl: normalizeReportUrl(out.pdfUrl),
  };
}

/**
 * Normaliza IDs de geocerca vindos do SelectField ou da URL.
 */
function normalizeIdArray(value) {
  const raw = Array.isArray(value) ? value : (value ? [value] : []);

  return raw
    .flatMap((item) => String(item ?? '').split(','))
    .map((item) => parseInt(String(item).trim(), 10))
    .filter(Number.isFinite);
}

function getGeofenceIdsFromSearch(searchParams) {
  return normalizeIdArray([
    ...searchParams.getAll('geofenceId'),
    ...searchParams.getAll('geofenceIds'),
  ]);
}

/**
 * Assinatura para identificar “o mesmo relatório”
 */
function makeSignature({ deviceId, from, to, geofenceIds, columns }) {
  const f = dayjs(from).toISOString();
  const t = dayjs(to).toISOString();
  const cols = [...(columns || [])].sort().join(',');
  const geos = [...new Set(geofenceIds || [])].sort((a, b) => a - b).join(',');
  return `${deviceId}|${f}|${t}|${geos}|${cols}`;
}

const RelatorioCompletoPage = () => {
  const { classes } = useReportStyles();
  const t = useTranslation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const geofenceIds = useMemo(
    () => getGeofenceIdsFromSearch(searchParams),
    [searchParams],
  );

  const userId = useSelector((state) => state.session?.user?.id);
  const devices = useSelector((state) => state.devices.items);

  // Colunas fixas
  const fixedColumns = useMemo(
    () => ['fixTime', 'latitude', 'longitude', 'speed', 'address'],
    [],
  );

  // histórico: {id, createdAt, htmlUrl?, pdfUrl?, status, attempts, payload, lastStatus, lastBody?, deviceIds, from, to, geofenceIds, signature, errorMessage?}
  const [history, setHistory] = useState([]);

  const [snack, setSnack] = useState({ open: false, text: '', severity: 'info' });

  const historyRef = useRef([]);
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  const pollersRef = useRef(new Map());

  const [previewPositions] = useState([]);
  const [selectedItem] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);

  const loadHistory = useCallback(() => {
    const raw = localStorage.getItem(STORAGE_KEY(userId));
    const parsed = safeJsonParse(raw || '[]', []);
    return Array.isArray(parsed) ? parsed : [];
  }, [userId]);

  const persistHistory = useCallback((next) => {
    setHistory(next);
    try {
      localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [userId]);

  useEffect(() => {
    persistHistory(loadHistory());
  }, [userId, loadHistory, persistHistory]);

  useEffect(() => () => {
    for (const [, timer] of pollersRef.current) clearInterval(timer);
    pollersRef.current.clear();
  }, []);

  const showSnack = useCallback((text, severity = 'info') => {
    setSnack({ open: true, text, severity });
  }, []);

  const updateEntry = useCallback((id, patch) => {
    setHistory((prev) => {
      const next = prev.map((h) => (h.id === id ? { ...h, ...patch } : h));
      try {
        localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(next));
      } catch {
        /* ignore */
      }
      historyRef.current = next;
      return next;
    });
  }, [userId]);

  const moveEntryToTop = useCallback((id) => {
    setHistory((prev) => {
      const idx = prev.findIndex((h) => h.id === id);
      if (idx < 0) return prev;
      const entry = prev[idx];
      const next = [entry, ...prev.slice(0, idx), ...prev.slice(idx + 1)].slice(0, HISTORY_LIMIT);
      try {
        localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(next));
      } catch {
        /* ignore */
      }
      historyRef.current = next;
      return next;
    });
  }, [userId]);

  const stopPolling = useCallback((entryId) => {
    const timer = pollersRef.current.get(entryId);
    if (timer) {
      clearInterval(timer);
      pollersRef.current.delete(entryId);
    }
  }, []);

  const startPolling = useCallback((entryId) => {
    if (pollersRef.current.has(entryId)) return;

    const timer = setInterval(async () => {
      const entry = historyRef.current.find((h) => h.id === entryId);

      if (!entry) {
        clearInterval(timer);
        pollersRef.current.delete(entryId);
        return;
      }

      if (entry.status !== 'pending') {
        clearInterval(timer);
        pollersRef.current.delete(entryId);
        return;
      }

      if ((entry.attempts ?? 0) >= MAX_ATTEMPTS) {
        updateEntry(entryId, {
          status: 'error',
          errorMessage: t('reportFullTimeoutError'),
        });
        clearInterval(timer);
        pollersRef.current.delete(entryId);
        showSnack(t('reportFullTimeoutTryAgain'), 'warning');
        return;
      }

      await tryGenerate(entryId, entry.payload, { silent: true });
    }, POLL_INTERVAL_MS);

    pollersRef.current.set(entryId, timer);
  }, [showSnack, updateEntry]);

  const tryGenerate = useCallback(async (entryId, payload, { silent = false } = {}) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      const resp = await fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      const text = await resp.text();
      const { htmlUrl, pdfUrl } = extractReportUrls(text, resp);

      if (htmlUrl) {
        updateEntry(entryId, {
          htmlUrl,
          pdfUrl: pdfUrl || '',
          status: 'ready',
          lastStatus: resp.status,
          lastBody: '',
          errorMessage: '',
        });
        stopPolling(entryId);

        navigate(buildViewerPath(htmlUrl, 'html'));

        if (!silent) showSnack(t('reportFullReadyOpening'), 'success');
        return;
      }

      const current = historyRef.current.find((h) => h.id === entryId);
      const attempts = (current?.attempts ?? 0) + 1;

      updateEntry(entryId, {
        attempts,
        status: 'pending',
        lastStatus: resp.status,
        lastBody: (text || '').slice(0, 2000),
        errorMessage: !silent && !resp.ok
          ? (normalizeBackendErrorMessage(text, t) || `${t('reportFullHttpFailurePrefix')} ${resp.status}`)
          : '',
      });

      if (!silent && !resp.ok) {
        updateEntry(entryId, { status: 'error' });
        const backendError = normalizeBackendErrorMessage(text, t);
        if (backendError) {
          showSnack(`${backendError}. ${t('reportFullTryAgain')}`, 'error');
        } else {
          showSnack(`${t('reportFullFailedGenerateHttp')} (${t('sharedHttp')} ${resp.status}). ${t('reportFullTryAgain')}`, 'error');
        }
      }
    } catch (e) {
      const isTimeout = e?.name === 'AbortError';
      const msg = isTimeout ? t('reportFullRequestTimeout') : ((e && e.message) ? e.message : t('reportFullUnknownError'));
      updateEntry(entryId, {
        status: 'error',
        errorMessage: msg,
      });
      if (!silent) showSnack(`${t('sharedError')}: ${msg}`, 'error');
    }
  }, [navigate, showSnack, stopPolling, updateEntry]);

  const handleOpenHtml = useCallback(async (entryId) => {
    const entry = historyRef.current.find((h) => h.id === entryId);
    if (!entry) return;

    const normalizedHtmlUrl = normalizeReportUrl(entry.htmlUrl);
    const normalizedPdfUrl = normalizeReportUrl(entry.pdfUrl);

    if (entry.status === 'ready' && normalizedHtmlUrl) {
      moveEntryToTop(entryId);
      navigate(buildViewerPath(normalizedHtmlUrl, 'html'));
      return;
    }

    if (entry.status === 'ready' && normalizedPdfUrl) {
      moveEntryToTop(entryId);
      navigate(buildViewerPath(normalizedPdfUrl, 'pdf'));
      showSnack(t('reportFullHtmlUnavailableOpeningPdf'), 'info');
      return;
    }

    if (entry.status === 'pending') {
      showSnack(t('reportFullWaitTryNow'), 'info');
      await tryGenerate(entryId, entry.payload, { silent: false });
      const latest = historyRef.current.find((h) => h.id === entryId);
      if (latest?.status === 'pending') startPolling(entryId);
      return;
    }

    showSnack(t('reportFullWaitRetrying'), 'info');
    updateEntry(entryId, { status: 'pending', errorMessage: '' });
    await tryGenerate(entryId, entry.payload, { silent: false });
    const latest = historyRef.current.find((h) => h.id === entryId);
    if (latest?.status === 'pending') startPolling(entryId);
  }, [moveEntryToTop, navigate, showSnack, startPolling, tryGenerate, updateEntry]);

  const handleOpenPdf = useCallback((entryId) => {
    const entry = historyRef.current.find((h) => h.id === entryId);
    if (!entry) return;
    if (entry.status === 'ready' && entry.pdfUrl) {
      moveEntryToTop(entryId);
      navigate(buildViewerPath(entry.pdfUrl, 'pdf'));
    }
  }, [moveEntryToTop, navigate]);

  const handleClearHistory = useCallback(() => {
    for (const [, timer] of pollersRef.current) clearInterval(timer);
    pollersRef.current.clear();
    persistHistory([]);
    showSnack(t('reportFullHistoryCleared'), 'success');
  }, [persistHistory, showSnack]);

  const onGenerate = useCatch(async ({ deviceIds, from, to }) => {
    const firstId = deviceIds?.[0];
    if (!firstId) return;

    showSnack(t('reportFullWaitMayTakeTime'), 'info');

    const device = devices?.[firstId];

    const fmt = 'YYYY-MM-DDTHH:mm';
    const payload = {
      placa: resolvePlaca(device, firstId),
      data_inicio: dayjs(from).format(fmt),
      data_fim: dayjs(to).format(fmt),
      geocercas: geofenceIds,
      selectedColumns: buildSelectedColumns(fixedColumns),
      gerarPdf: true,
    };

    const signature = makeSignature({
      deviceId: firstId,
      from,
      to,
      geofenceIds,
      columns: fixedColumns,
    });

    const existingReady = historyRef.current.find(
      (h) => h.signature === signature && h.status === 'ready' && h.htmlUrl,
    );
    if (existingReady) {
      moveEntryToTop(existingReady.id);
      navigate(buildViewerPath(existingReady.htmlUrl, 'html'));
      showSnack(t('reportFullAlreadyExistsOpening'), 'success');
      return;
    }

    const existingPending = historyRef.current.find(
      (h) => h.signature === signature && h.status === 'pending',
    );
    if (existingPending) {
      moveEntryToTop(existingPending.id);
      startPolling(existingPending.id);
      showSnack(t('reportFullAlreadyProcessingWait'), 'info');
      return;
    }

    const entryId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const entry = {
      id: entryId,
      createdAt: new Date().toISOString(),
      htmlUrl: '',
      pdfUrl: '',
      status: 'pending',
      attempts: 0,
      payload,
      deviceIds: [firstId],
      from,
      to,
      geofenceIds,
      signature,
      lastStatus: undefined,
      lastBody: '',
      errorMessage: '',
    };

    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, HISTORY_LIMIT);
      try {
        localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(next));
      } catch {
        /* ignore */
      }
      historyRef.current = next;
      return next;
    });

    await tryGenerate(entryId, payload, { silent: true });

    const latest = historyRef.current.find((h) => h.id === entryId);
    if (latest && latest.status === 'pending') startPolling(entryId);
  });

  const onMapPointClick = useCallback(() => {}, []);
  const mapDevices = useMemo(
    () => [...new Set(previewPositions.map((it) => it.deviceId))],
    [previewPositions],
  );

  const statusLabel = useCallback((h) => {
    if (h.status === 'ready') return 'Pronto';
    if (h.status === 'pending') return `Em processamento… (${h.attempts ?? 0}/${MAX_ATTEMPTS})`;
    if (h.status === 'error') return h.errorMessage ? `Falhou: ${h.errorMessage}` : 'Falhou';
    return '-';
  }, []);

  const actionLabel = useCallback((h) => {
    if (h.status === 'ready') return 'Abrir';
    if (h.status === 'pending') return 'Tentar agora';
    if (h.status === 'error') return 'Tentar novamente';
    return 'Abrir';
  }, []);

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportRelatorioCompleto']}>
      <div className={classes.container}>
        <ReportMapPanel
          title={t('reportRelatorioCompleto')}
          show={previewPositions.length > 0}
          onExpandedChange={setMapExpanded}
        >
          <ReportMapContent camera={Boolean(previewPositions.length)} cameraProps={{ positions: previewPositions }}>
            {mapDevices.map((deviceId) => {
              const positions = previewPositions.filter((p) => p.deviceId === deviceId);
              return (
                <Fragment key={deviceId}>
                  <MapRoutePath positions={positions} />
                  <MapRoutePoints positions={positions} onClick={onMapPointClick} />
                </Fragment>
              );
            })}
            {selectedItem && <MapPositions positions={[selectedItem]} titleField="fixTime" />}
          </ReportMapContent>
        </ReportMapPanel>

        <div
          className={classes.containerMain}
          data-has-map={previewPositions.length > 0}
          data-map-expanded={mapExpanded}
        >
          <div className={classes.header}>
            <ReportFilter
              onShow={onGenerate}
              onExport={null}
              onSchedule={null}
              deviceType="single"
              loading={false}
              showLabel={t('reportFullGenerate')}
            >
              <div className={classes.filterItem}>
                <SelectField
                  value={geofenceIds}
                  onChange={(e) => {
                    const values = normalizeIdArray(e.target.value).map(String);
                    updateReportParams(searchParams, setSearchParams, 'geofenceId', values);
                  }}
                  endpoint="/api/geofences"
                  label={t('sharedGeofence')}
                  multiple
                  fullWidth
                />
              </div>

              <Box mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {t('reportFullWaitMayTakeTime')}
                </Typography>
              </Box>
            </ReportFilter>
          </div>

          <Box px={2} pb={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="subtitle1">
                {t('reportFullHistoryTitle')}
              </Typography>

              <Tooltip title={t('reportFullHistoryClearTooltip')}>
                <Button size="small" onClick={handleClearHistory}>
                  {t('reportFullClearHistory')}
                </Button>
              </Tooltip>
            </Stack>
          </Box>

          <div className={classes.tableCard}>
            <div className={classes.tableScroller} data-has-map={previewPositions.length > 0} data-map-expanded={mapExpanded}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Abrir</TableCell>
                    <TableCell>PDF</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Dispositivo</TableCell>
                    <TableCell>Período</TableCell>
                    <TableCell>Geocerca</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary">Nenhum relatório gerado ainda.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell>{statusLabel(h)}</TableCell>

                        <TableCell>
                          {h.status === 'ready' && (normalizeReportUrl(h.htmlUrl) || normalizeReportUrl(h.pdfUrl)) ? (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Button size="small" onClick={() => handleOpenHtml(h.id)}>
                                Abrir
                              </Button>
                              {normalizeReportUrl(h.htmlUrl) && (
                                <Link
                                  href={normalizeReportUrl(h.htmlUrl)}
                                  underline="hover"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    moveEntryToTop(h.id);
                                    navigate(buildViewerPath(h.htmlUrl, 'html'));
                                  }}
                                  sx={{ fontSize: 12 }}
                                >
                                  link
                                </Link>
                              )}
                            </Stack>
                          ) : (
                            <Button size="small" onClick={() => handleOpenHtml(h.id)}>
                              {actionLabel(h)}
                            </Button>
                          )}
                        </TableCell>

                        <TableCell>
                          {h.status === 'ready' && h.pdfUrl ? (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Button size="small" onClick={() => handleOpenPdf(h.id)}>
                                PDF
                              </Button>
                              <Link
                                href={normalizeReportUrl(h.pdfUrl)}
                                underline="hover"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    moveEntryToTop(h.id);
                                    navigate(buildViewerPath(h.pdfUrl, 'pdf'));
                                  }}
                                  sx={{ fontSize: 12 }}
                                >
                                link
                              </Link>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>

                        <TableCell>{new Date(h.createdAt).toLocaleString()}</TableCell>
                        <TableCell>{(h.deviceIds || []).join(', ')}</TableCell>
                        <TableCell>
                          {dayjs(h.from).format('YYYY-MM-DD HH:mm')}
                          {' '}
                          →{' '}
                          {dayjs(h.to).format('YYYY-MM-DD HH:mm')}
                        </TableCell>
                        <TableCell>{h.geofenceIds?.length ? h.geofenceIds.join(', ') : '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <Snackbar
            open={snack.open}
            autoHideDuration={3500}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnack((s) => ({ ...s, open: false }))}
              severity={snack.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snack.text}
            </Alert>
          </Snackbar>
        </div>
      </div>
    </PageLayout>
  );
};

export default RelatorioCompletoPage;
